import { FunctionComponent, MouseEventHandler, useRef } from "react";

import { faClock } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { ToggleColor } from "./types";
import { Icon } from "../Icon/Icon";

interface ToggleProps {
	labelClass?: string;
	children?: JSX.Element | JSX.Element[] | string;
	isChecked: boolean;
	onChange?: MouseEventHandler;
	showError?: boolean;
	error?: boolean | string;
	testId?: string;
	className?: string;
	name?: string;
	colorfulToggle?: string;
	color?: ToggleColor;
	disabled?: boolean;
}

export const Toggle: FunctionComponent<ToggleProps> = ({
	children,
	isChecked,
	onChange,
	showError,
	error,
	labelClass = "",
	testId,
	className,
	name,
	color = ToggleColor.ONCE,
	colorfulToggle = "",
	disabled = false,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	return (
		<div
			className={classNames(
				className || "",
				"flex items-center min-w-[38px] w-[38px] bg-gray-200 px-[4px] min-h-[22px]  h-[22px] rounded-full duration-200",
				isChecked ? "justify-end" : "",
				disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
				colorfulToggle.length > 0 && isChecked ? `bg-status-${colorfulToggle}` : ""
			)}
			data-test={testId}
			onClick={(e) => {
				if (disabled) {
					return;
				}

				// A <label> around the Toggle forwards every click inside it to the hidden checkbox below,
				// and that click bubbles back here: a click on the switch arrives twice, a click on the
				// label's text once, always as the forwarded one. So when a label controls this checkbox,
				// count only the forwarded click (PREC-4401: the "Email Signature" switch never moved).
				const input = inputRef.current;
				const labelForwardsHere =
					input !== null && e.currentTarget.closest("label")?.control === input;

				if (labelForwardsHere && e.target !== input) {
					return;
				}

				onChange?.(e);
			}}
		>
			<div
				className={classNames(
					"w-4 text-white bg-white h-4 flex items-center justify-center rounded-full duration-200",
					isChecked ? "!bg-brand-light" : "border-gray-200 hover:bg-gray-100",
					showError && !!error ? "!border-status-error" : "",
					disabled ? "cursor-not-allowed" : "cursor-pointer",
					colorfulToggle.length > 0 && isChecked ? `!bg-white` : ""
				)}
			>
				{color === ToggleColor.ON_GOING ? (
					<Icon
						className={classNames(isChecked ? "text-white" : "text-status-info")}
						icon={faClock}
					/>
				) : (
					""
				)}
			</div>
			<input
				ref={inputRef}
				readOnly
				aria-label={name || "toggle"}
				className="hidden"
				name={name || ""}
				type="checkbox"
			/>
			{children && (
				<label className={classNames("ml-3 -mt-0.5 text-sm text-gray-700", labelClass)}>
					{" "}
					{children}
				</label>
			)}
		</div>
	);
};
